import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Paper, Typography } from "@mui/material";

import {
  getUserPreferenceValue,
  selectCurrentAccount,
  selectFolderById,
  useGetAccountsQuery,
  useGetUserMeAccountsQuery,
  useGetUserMeQuery,
  useUpdateUserPreferencesMutation,
} from "fond/api";
import { useContents } from "fond/hooks/useContents";
import ProjectList from "fond/projects/ProjectList/ProjectList";
import ProjectListHeader from "fond/projects/ProjectListHeader";
import SearchBar from "fond/projects/SearchBar";
import { FinancialAnalyticsIntroTour } from "fond/tours";
import { RecentlyViewedEntity, ResourceEntity, Store, UserPreferenceKey, UserPreferenceSubKey } from "fond/types";
import { NoAccountContentPanel } from "fond/widgets/NoAccountContentPanel/NoAccountContentPanel";

interface RouteParams {
  folderId: string;
  multiProjectFolderId: string;
}

interface BaseProjectListProps {
  items: ResourceEntity[] | RecentlyViewedEntity<ResourceEntity>[];
}

/**
 * A main page content displaying folders and projects.
 * Includes a DataGrid supporting column sorting and pagination
 */
const BaseProjectList: React.FC<BaseProjectListProps> = ({ items }) => {
  const { folderId: routerFolderId, multiProjectFolderId } = useParams<keyof RouteParams>();
  const folderId = routerFolderId || multiProjectFolderId;

  const folder = useSelector((state: Store) => (folderId ? selectFolderById(state, folderId) : undefined));
  const account = useSelector(selectCurrentAccount);

  const { isLoading, isFetching: isFetchingContent, items: allItems } = useContents();
  const { isFetching: isFetchingAccounts } = useGetAccountsQuery();
  const { isFetching: isFetchingMe } = useGetUserMeQuery();
  const { isFetching: isFetchingMeAccounts } = useGetUserMeAccountsQuery();

  const [updatePreference] = useUpdateUserPreferencesMutation();
  const showFinancialAnalyticsIntoTour = useSelector((state: Store) =>
    getUserPreferenceValue(state, UserPreferenceKey.UI_TOUR, UserPreferenceSubKey.FINANCIAL_ANALYTICS_INTRO)
  );

  const isFetching = isFetchingMeAccounts || isFetchingAccounts || isFetchingMe || isFetchingContent;

  const [searchText, setSearchText] = useState("");

  /**
   * Callback function that is passed the filter text as it changes
   */
  const handleOnFilterChange = (value: string) => {
    setSearchText(value.toUpperCase());
  };

  return (
    <>
      {folder ? <Typography style={{ whiteSpace: "pre-line" }}>{folder.Description}</Typography> : <ProjectListHeader />}
      {!account && allItems.length === 0 ? (
        <NoAccountContentPanel />
      ) : (
        <>
          <Box my={3}>
            <SearchBar onChange={handleOnFilterChange} placeholder="Filter items..." />
          </Box>
          <Paper square>
            <ProjectList searchText={searchText} items={isLoading ? null : items} folder={folder} isLoading={isFetching} />
          </Paper>
        </>
      )}

      {showFinancialAnalyticsIntoTour && (
        <FinancialAnalyticsIntroTour
          onComplete={() => {
            updatePreference({ Key: UserPreferenceKey.UI_TOUR, Subkey: UserPreferenceSubKey.FINANCIAL_ANALYTICS_INTRO, Value: false });
          }}
        />
      )}
    </>
  );
};

export default BaseProjectList;
